/**
 * Analytics Panel Component
 * Displays comprehensive analytics for an AI system
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  PlayArrow as ExecutionIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Schedule as ScheduleIcon,
  Person as ExpertIcon,
} from '@mui/icons-material';
import { aiSystemApi } from '../../services/api';
import type { AISystemAnalyticsResponse } from '../../types';

interface AnalyticsPanelProps {
  systemId: number;
}

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({ systemId }) => {
  const [periodDays, setPeriodDays] = useState<number>(30);

  const { data, isLoading, error } = useQuery<AISystemAnalyticsResponse>({
    queryKey: ['ai-system-analytics', systemId, periodDays],
    queryFn: () => aiSystemApi.getAnalytics(systemId, periodDays),
    enabled: !!systemId,
    staleTime: 60000, // 1 minute
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load analytics: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No analytics data available.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Period Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">System Analytics</Typography>
        <ToggleButtonGroup
          value={periodDays}
          exclusive
          onChange={(_, value) => value && setPeriodDays(value)}
          size="small"
        >
          <ToggleButton value={7}>7 Days</ToggleButton>
          <ToggleButton value={30}>30 Days</ToggleButton>
          <ToggleButton value={90}>90 Days</ToggleButton>
          <ToggleButton value={365}>1 Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ExecutionIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Executions
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 1 }}>
                {data.executions.total.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.executions.recent} in last {data.period_days} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SuccessIcon color="success" />
                <Typography variant="subtitle2" color="text.secondary">
                  Success Rate
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 1 }}>
                {data.executions.success_rate}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.executions.success_rate}
                color={data.executions.success_rate >= 90 ? 'success' : data.executions.success_rate >= 70 ? 'warning' : 'error'}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon color="warning" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Cost
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 1 }}>
                ${data.costs.total.toFixed(4)}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="info.main">
                  Experts: ${(data.costs.experts_cost || 0).toFixed(4)}
                </Typography>
                <Typography variant="body2" color="warning.main">
                  Compute: ${(data.costs.compute_cost || 0).toFixed(6)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                ${data.costs.recent.toFixed(4)} in last {data.period_days} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Avg Cost/Execution
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 1 }}>
                ${data.costs.average_per_execution.toFixed(4)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Trade Stats Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>Trading Performance</Typography>
      {data.trades ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Total Trades</Typography>
                  <Typography variant="h6">{data.trades.total_trades}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Win Rate</Typography>
                  <Typography variant="h6" color={data.trades.win_rate >= 50 ? 'success.main' : 'error.main'}>
                    {data.trades.win_rate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Total P/L</Typography>
                  <Typography variant="h6" color={data.trades.total_pl >= 0 ? 'success.main' : 'error.main'}>
                    ${data.trades.total_pl.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Profit Factor</Typography>
                  <Typography variant="h6" color={data.trades.profit_factor >= 1 ? 'success.main' : 'error.main'}>
                    {data.trades.profit_factor}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Avg Win/Loss</Typography>
                  <Typography variant="body2">
                    <span style={{ color: 'green' }}>${data.trades.avg_win}</span>
                    {' / '}
                    <span style={{ color: 'red' }}>${Math.abs(data.trades.avg_loss)}</span>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No trades have been created by this AI system yet. Trades will appear here once the system executes trading actions.
        </Alert>
      )}
      <Divider sx={{ my: 3 }} />

      {/* Status Breakdown */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Execution Status Breakdown</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {Object.entries(data.executions.status_breakdown).map(([status, count]) => (
              <Box key={status} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  label={status}
                  size="small"
                  color={
                    status === 'SUCCESS' ? 'success' :
                    status === 'ERROR' ? 'error' :
                    status === 'RUNNING' ? 'primary' :
                    'default'
                  }
                  sx={{ width: 100, mr: 2 }}
                />
                <Box sx={{ flex: 1, mr: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(count / data.executions.recent) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>
                  {count}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Top Errors</Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
            {data.errors.top_errors.length === 0 ? (
              <Alert severity="success" icon={<SuccessIcon />}>
                No errors in the selected period!
              </Alert>
            ) : (
              <List dense disablePadding>
                {data.errors.top_errors.map((err, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={err.message}
                      secondary={`${err.count} occurrence${err.count > 1 ? 's' : ''}`}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Expert Performance Table */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Expert Performance</Typography>
      {data.experts.length === 0 ? (
        <Alert severity="info">No expert execution data available.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Expert</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="right">Executions</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Evaluator</TableCell>
                <TableCell align="right">Avg Accuracy</TableCell>
                <TableCell align="right">Avg Hallucination</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell align="right">Compute Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.experts.map((expert) => (
                <TableRow key={expert.expert_id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ExpertIcon fontSize="small" color="action" />
                      <Typography variant="body2">{expert.expert_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={expert.expert_type}
                      size="small"
                      color={
                        expert.expert_type === 'ANALYSIS' ? 'info' :
                        expert.expert_type === 'ACTION' ? 'success' :
                        expert.expert_type === 'AGENT' ? 'secondary' :
                        'warning'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{expert.execution_count}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${expert.success_rate}%`}
                      size="small"
                      color={expert.success_rate >= 90 ? 'success' : expert.success_rate >= 70 ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {expert.use_evaluator ? (
                      <Chip
                        label={`${expert.evaluator_count} (${expert.evaluator_success_rate}%)`}
                        size="small"
                        color={expert.evaluator_success_rate >= 90 ? 'success' : expert.evaluator_success_rate >= 70 ? 'warning' : 'error'}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {expert.avg_accuracy !== null ? (
                      <Chip
                        label={`${expert.avg_accuracy}%`}
                        size="small"
                        color={expert.avg_accuracy >= 80 ? 'success' : expert.avg_accuracy >= 60 ? 'warning' : 'error'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {expert.avg_hallucination !== null ? (
                      <Chip
                        label={`${expert.avg_hallucination}%`}
                        size="small"
                        color={expert.avg_hallucination <= 20 ? 'success' : expert.avg_hallucination <= 40 ? 'warning' : 'error'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">${expert.total_cost.toFixed(4)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="warning.main">
                      ${(expert.compute_cost || 0).toFixed(6)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Daily Trends */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Daily Trends</Typography>
      {data.daily_trends.length === 0 ? (
        <Alert severity="info">No execution data for the selected period.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Executions</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Experts Cost</TableCell>
                <TableCell align="right">Compute Cost</TableCell>
                <TableCell align="right">Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.daily_trends.slice().reverse().map((day) => (
                <TableRow key={day.date} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{day.executions}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main">{day.success}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={day.error > 0 ? 'error.main' : 'text.secondary'}>
                      {day.error}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="info.main">
                      ${(day.experts_cost || 0).toFixed(4)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="warning.main">
                      ${(day.compute_cost || 0).toFixed(6)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">${day.cost.toFixed(4)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AnalyticsPanel;
